import React from "react";
import { Shield, Sparkles, AlertTriangle, LogOut } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

interface NexoraXLegalFooterProps {
  onNavigate: (route: string) => void;
  activeRoute: string | null;
}

export function NexoraXLegalFooter({ onNavigate, activeRoute }: NexoraXLegalFooterProps) {
  const { user, logout } = useAuth();
  
  const links = [
    { title: "Privacy Policy", route: "/privacy-policy" },
    { title: "Cookie Policy", route: "/cookie-policy" },
    { title: "Terms & Conditions", route: "/terms" },
    { title: "Refund Policy", route: "/refund-policy" },
    { title: "Cancellation Policy", route: "/cancellation-policy" },
    { title: "Cookie Settings", route: "/cookie-settings" },
    { title: "Accessibility", route: "/accessibility" },
    { title: "Privacy Rights Request", route: "/privacy-rights-request" },
    { title: "Contact", route: "/contact" }
  ];

  const handleLinkClick = (e: React.MouseEvent, route: string) => {
    e.preventDefault();
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", route);
      // Dispatch custom route transition event
      window.dispatchEvent(new Event("popstate"));
    }
    onNavigate(route);
    // Smooth scroll to top of document viewer
    const el = document.getElementById("nexorax-legal-documents-parent");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFooterSignOut = () => {
    const triggerSeverance = async () => {
      try {
        await logout();
        sessionStorage.removeItem("nexorax_mfa_verified");
        window.dispatchEvent(new CustomEvent("nexorax-global-logout"));
      } catch (err) {
        console.error("Footer logout failed:", err);
      }
    };

    if (window.confirmNexoraXAction) {
      window.confirmNexoraXAction({
        title: "CONFIRM SESSION SEVERANCE",
        message: "You are about to terminate the active secure connection to the NexoraX Neural Core from the platform footer. This will revoke credentials, secure routing logs, and return to the gateway.",
        confirmLabel: "SEVER PROTOCOL",
        cancelLabel: "ABORT DISCONNECT",
        impactLevel: "HIGH",
        nodeName: "FOOTER OVERWATCH TERMINATOR"
      }, triggerSeverance);
    } else {
      if (window.confirm("NEXORAX TERMINATION PROTOCOL:\n\nAre you sure you want to sign out and log out?")) {
        triggerSeverance();
      }
    }
  };

  return (
    <footer id="nexorax-legal-footer" className="border-t border-purple-500/25 bg-black/85 backdrop-blur-md py-10 px-6 mt-12 w-full relative shrink-0">
      {/* Gold Divider Accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-[1.5px] bg-gradient-to-r from-transparent via-yellow-500/60 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto flex flex-col items-center space-y-8 text-center">
        {/* Brand Signpost */}
        <div className="flex items-center gap-2">
          <img
            src="/logo.png?v=2"
            alt="NexoraX"
            className="h-6 w-auto drop-shadow-[0_0_8px_rgba(0,240,255,0.6)]"
          />
          <span className="font-sans font-black text-xs uppercase tracking-[0.4em] logo-gradient-text no-sweep drop-shadow-[0_0_8px_rgba(168,85,247,0.4)]">
            NEXORAX LEGAL FOOTER
          </span>
        </div>

        {/* Links Navigation Grid */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 font-mono text-[9.5px] uppercase tracking-wider">
          {links.map((link) => {
            const isMatch = activeRoute === link.route;
            return (
              <a
                key={link.route}
                id={`footer-link-${link.route.replace('/', '')}`}
                href={link.route}
                onClick={(e) => handleLinkClick(e, link.route)}
                className={`transition-colors duration-200 cursor-pointer ${
                  isMatch 
                  ? "text-[#00ffd2] font-extrabold drop-shadow-[0_0_5px_rgba(0,255,255,0.4)]" 
                  : "text-slate-400 hover:text-[#00ffd2] hover:drop-shadow-[0_0_5px_rgba(0,255,255,0.3)]"
                }`}
              >
                {link.title}
              </a>
            );
          })}
        </div>

        {/* Sign Out call-to-action button when logged in */}
        {user && (
          <div className="pt-2">
            <button
              onClick={handleFooterSignOut}
              className="px-6 py-2.5 bg-rose-950/20 hover:bg-rose-900/40 border border-rose-500/30 hover:border-rose-400 text-rose-400 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 shadow-[0_0_15px_rgba(244,63,94,0.1)] hover:shadow-[0_0_25px_rgba(244,63,94,0.25)] flex items-center gap-2 cursor-pointer active:scale-97"
            >
              <LogOut className="w-3.5 h-3.5 text-rose-400 shrink-0" />
              <span>TERMINATE CLIENT SESSION (SIGN OUT)</span>
            </button>
          </div>
        )}

        {/* Informative Disclaimer Card */}
        <div className="max-w-3xl rounded-2xl bg-purple-950/10 border border-purple-500/10 p-4 font-mono text-[9px] text-slate-500 leading-relaxed text-center space-y-1.5 shadow-[inset_0_0_15px_rgba(168,85,247,0.05)]">
          <div className="flex items-center justify-center gap-2 text-yellow-500/60 font-black uppercase text-[8px] tracking-widest">
            <AlertTriangle className="w-3.5 h-3.5" />
            Public Regulatory Disclaimer
          </div>
          <p>
            Privacy, consent, and accounting-readiness tools are provided for public preview. Professional review is recommended before legal reliance, live billing, VAT filing, or tax submissions.
          </p>
        </div>

        {/* Copyright Notice */}
        <div className="text-[9.5px] font-mono text-zinc-650 uppercase tracking-widest border-t border-white/5 pt-4 w-full">
          © NexoraX Digital Ltd. All rights reserved. Registered SC887095.
        </div>

        {/* Reset Lock Storage Trigger */}
        <div className="pt-2">
          <button
            onClick={() => {
              const keys = [
                'nexorax_secure_access_gate_v1',
                'nexorax_secure_access_gate_v1_startedAt',
                'nexorax_mfa_verified',
                'nexorax_bypassed_user_email'
              ];
              keys.forEach(k => {
                localStorage.removeItem(k);
                sessionStorage.removeItem(k);
              });
              alert("Lock storage cleared! Please refresh the page.");
            }}
            className="text-[9px] text-slate-600 hover:text-[#00ffd2] font-mono uppercase tracking-widest cursor-pointer underline"
          >
            Reset Client Lock Storage
          </button>
        </div>
      </div>
    </footer>
  );
}
